import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Notes modal launched from a calendar cell's "Notes" link.
 *
 * - Save button is sticky at the top so it stays in view no matter
 *   how long the note grows.
 * - Saving an empty string clears the note (the cell's lime-green
 *   circle flips back to outline form).
 */
export default function MedNotesModal({ date, initialNote, onSave, onClose }) {
  const [text, setText] = useState(initialNote || '');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Focus the textarea on open and place the caret at the end so
    // the user can immediately continue writing.
    if (textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      try { textareaRef.current.setSelectionRange(len, len); } catch (_e) { /* noop */ }
    }
  }, []);

  // Per owner spec: the notes modal must NOT close on outside click,
  // Escape, or accidental mouse movement. The only exits are the Save
  // button and the X button in the top-right of the sticky header.

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(text);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="med-notes-modal-backdrop"
    >
      <div
        className="w-full max-w-lg bg-white rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
        role="dialog"
        aria-modal="true"
        aria-label={`Notes for ${date}`}
        data-testid="med-notes-modal"
      >
        {/* Sticky header with Save + Close, always visible. */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white">
          <div className="text-sm font-semibold text-slate-700">
            Notes · <span className="tabular-nums">{formatDate(date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="text-sm px-3 py-1.5 rounded bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-60"
              data-testid="med-notes-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-slate-100 text-slate-500"
              aria-label="Close"
              data-testid="med-notes-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-4 flex-1 overflow-hidden flex flex-col">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a note for this day…"
            className="w-full flex-1 min-h-[240px] resize-none rounded border border-slate-300 p-3 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
            data-testid="med-notes-textarea"
          />
          <p className="mt-2 text-[11px] text-slate-500">
            Saving an empty note clears the day's notepad.
          </p>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return `${m}/${d}/${y}`;
}
